import {
  useWorkflowDelete,
  useWorkflowDuplicate,
  useWorkflowUpdateIsActive,
} from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

interface WorkflowActivationTarget {
  workflowId: number;
  currentWorkflowVersionId: number;
  is_active: boolean;
}

interface WorkflowActionsOptions {
  onDeleteSuccess?: (workflowId: number) => Promise<void> | void;
}

export const useWorkflowActions = ({ onDeleteSuccess }: WorkflowActionsOptions = {}) => {
  const navigate = useNavigate();
  const workflowDelete = useWorkflowDelete();
  const workflowDuplicate = useWorkflowDuplicate();
  const workflowUpdateIsActive = useWorkflowUpdateIsActive();

  const editWorkflow = useCallback(
    (workflowId: number) => {
      navigate(`/workflows/${workflowId}/setup/details`);
    },
    [navigate]
  );

  const toggleWorkflowIsActive = useCallback(
    async (workflow: WorkflowActivationTarget) => {
      try {
        await workflowUpdateIsActive.mutateAsync({
          workflowId: workflow.workflowId,
          workflowVersionId: workflow.currentWorkflowVersionId,
          payload: !workflow.is_active,
        });

        analyticsService.trackEvent(
          workflow.is_active
            ? analyticEvents.workflows.deactivated
            : analyticEvents.workflows.activated,
          {
            [analyticProperties.id]: workflow.workflowId,
          }
        );
        return true;
      } catch {
        return false;
      }
    },
    [workflowUpdateIsActive]
  );

  const deleteWorkflow = useCallback(
    async (workflowId: number) => {
      try {
        await workflowDelete.mutateAsync({ workflowId });
        analyticsService.trackEvent(analyticEvents.workflows.deleted, {
          [analyticProperties.id]: workflowId,
        });
        await onDeleteSuccess?.(workflowId);
      } catch {
        return;
      }
    },
    [onDeleteSuccess, workflowDelete]
  );

  const duplicateWorkflow = useCallback(
    async (workflowId: number) => {
      try {
        await workflowDuplicate.mutateAsync({ workflowId });
        analyticsService.trackEvent(analyticEvents.workflows.duplicated, {
          [analyticProperties.id]: workflowId,
        });
      } catch {
        return;
      }
    },
    [workflowDuplicate]
  );

  return {
    editWorkflow,
    toggleWorkflowIsActive,
    isWorkflowActivationPending: workflowUpdateIsActive.isPending,
    deleteWorkflow,
    isWorkflowDeletePending: workflowDelete.isPending,
    duplicateWorkflow,
    isWorkflowDuplicatePending: workflowDuplicate.isPending,
  };
};
